<template>
  <drawer v-bind="$attrs" v-model="modelValue" no-footer width="500">
    <template v-slot:title>
      <tips-icon class="mr-2" />
      What's New
    </template>
    <div class="pa-6">
      <news-entry
        v-for="entry in entries"
        :key="isoDate(entry.date)"
        v-bind="entry"
        @click="modelValue = false"
      />
    </div>
  </drawer>
</template>

<script setup lang="ts">
import { DateTime } from '@quotetome/materials-api'
import Drawer from '@/components/drawer.vue'
import NewsEntry, { type Props as Entry } from '@/components/news/news-entry.vue'
import TipsIcon from '@/components/news/tips-icon.vue'
import { newNews } from './news-store'
import { useAuthStore } from '@/stores/auth'
import { useStorage } from '@vueuse/core'
import { isoDate } from '@/models/filters'

const modelValue = defineModel<boolean>({ default: false })

const authStore = useAuthStore()
const userCompany = authStore.userCompany!

const entries: Entry[] = [
  {
    date: new DateTime('2026-03-11'),
    sections: [
      {
        content: 'Meet legal requirements and boost accountability by collecting electronic signatures from suppliers'
          +  ' before POs are issued. Require signatures on every PO, or only the ones that need it.',
      },
      {
        content: 'Suppliers review and sign online—no software or login needed'
          + '. Once both parties have signed, a PO number is generated and the order is issued.',
        images: ['/news/PO_Supplier_Signing_Modal.png'],
      },
      {
        images: ['/news/Signed_PO_PDF.png'],
      },
      {
        content: 'Learn more and turn it on in <b>Company Settings > Purchase Order Settings</b>.',
      },
    ],
    title: 'Get PO sign-off from suppliers—the simple way',
  },
  {
    date: new DateTime('2025-06-16'),
    sections: [
      {
        content: 'Create Hold-for-Release POs and manage deliveries by item and date, all from the field.',
        images: ['/news/hold-for-release-po.png'],
      },
      {
        content: 'Update the QuoteToMe mobile app and start releasing materials today:',
        images: ['/news/hold-for-release-mobile-1.png', '/news/hold-for-release-mobile-2.png'],
      },
    ],
    title: "Release materials when you're ready",
  },
  {
    date: new DateTime('2025-03-31'),
    sections: [
      {
        content: "Manage your job's BOM from your mobile app—add materials, track orders, and collaborate in real time."
          + ' No computer needed on-site. Update your app to get started!',
        images: ['/news/bom-mobile-1.png', '/news/bom-mobile-2.png'],
      },
    ],
    title: '📱 Bill of Materials is now available on mobile!',
  },
  {
    date: new DateTime('2025-02-18'),
    link: {
      text: 'Explore Suppliers',
      to: { name: 'companies-id-suppliers', params: { id: userCompany.id! } }
    },
    noMobile: true,
    sections: [
      {
        content: 'Easily review your company suppliers. '
          + 'Edit or add preferred contacts to keep procurement running smoothly.',
        images: ['/news/suppliers-page.png'],
      },
    ],
    title: 'Introducing the new Suppliers Page',
  },
]

const dateSeen = useStorage<string | null>('news-date-seen', null)

onMounted(() => {
  setTimeout(() => {
    const latestDate = entries[0].date

    if (!dateSeen.value || new DateTime(dateSeen.value).isBefore(latestDate)) {
      newNews.value = true
    }
  }, 2000)
})

watch(modelValue, () => {
  if (modelValue.value && newNews.value) {
    dateSeen.value = isoDate(entries[0].date)
    newNews.value = false
  }
})
</script>
